import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  applyQuickMath,
  canStartQuickMath,
  isQuickMathOperator,
  parseQuickMathOperand,
  resolveQuickMathBaseValue,
  type QuickMathOperator
} from './quick-math';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatDateValue, FormDatePickerV2 } from '../form-field-v2/date-picker';
import { useInputTableEditingContext } from '../input-table-v2/editing-context';
import { Checkbox } from '@/components/ui/checkbox';
import { formatMoney } from '@/lib/formatUtils';
import type { CellFieldV2Props } from './types';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

const baseFieldClassName =
  'h-[var(--erp-control-height)] w-full rounded-none border-0 border-b border-b-gray-200 bg-transparent px-2 py-0 text-[14px] shadow-none focus-visible:border-b-black focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-black focus-visible:ring-offset-0';
const errorFieldClassName = 'border-b-red-500 focus-visible:border-b-red-600';
const errorTooltipClassName = 'z-[99999] border-none bg-[#333] px-2 py-1 text-xs text-white';
const quickMathPopoverClassName = 'w-[176px] rounded-sm border border-gray-200 bg-white p-2 shadow-lg';
const quickMathHistoryRowClassName = 'flex items-center justify-between gap-2 text-[12px] leading-none text-black';
const quickMathHistoryOperatorClassName = 'w-3 shrink-0 text-[12px] font-semibold text-black';
const quickMathHistoryValuesClassName = 'flex items-center justify-end gap-2';
const quickMathHistoryOperandClassName = 'text-[12px] font-medium text-black';
const quickMathHistoryResultClassName = 'text-[12px] font-bold text-black';
const quickMathCurrentRowClassName = 'flex items-center justify-between gap-2 text-[12px] leading-none';
const quickMathCurrentOperatorClassName = 'w-3 shrink-0 text-[16px] font-semibold leading-none text-red-600';
const quickMathCurrentResultClassName = 'mt-1.5 text-center text-[12px] font-bold text-black';

interface QuickMathStep {
  operator: QuickMathOperator;
  operand: number;
  result: number;
}

const getCellOptionKey = (option: { value: unknown }, index: number) => `${String(option.value)}-${index}`;

function CellFieldV2Component<T = any>({
  value,
  onValueChange,
  onCommit,
  type = 'text',
  disabled = false,
  options,
  error,
  errorDisplay = 'message',
  className,
  placeholder,
  inputClassName,
  commitOnBlur = false,
  disableQuickMath = false
}: CellFieldV2Props<T>) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { enableNumberQuickMath } = useInputTableEditingContext();
  const fieldError = error;
  const [displayValue, setDisplayValue] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);
  const [multiselectOpen, setMultiselectOpen] = useState(false);
  const [calculator, setCalculator] = useState<{
    open: boolean;
    operator: QuickMathOperator | null;
    baseValue: number | null;
    steps: QuickMathStep[];
  }>({
    open: false,
    operator: null,
    baseValue: null,
    steps: []
  });
  const shouldRenderErrorTooltip = Boolean(fieldError) && errorDisplay === 'tooltip';
  const quickMathEnabled = type === 'number' && !disabled && !disableQuickMath && enableNumberQuickMath;
  const withErrorTooltip = (children: React.ReactElement) => {
    if (!shouldRenderErrorTooltip) {
      return children;
    }

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{children}</TooltipTrigger>
          <TooltipContent side='top' align='start' className={errorTooltipClassName}>
            {fieldError}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const formatNumericValue = useCallback(
    (nextValue: T | undefined) => formatMoney((nextValue as string | number | undefined) ?? ''),
    []
  );

  const parseNumber = (formattedValue: string): number | null => {
    if (!formattedValue) return null;
    const cleanValue = formattedValue.replace(/[^\d.-]/g, '');
    return parseFloat(cleanValue) || null;
  };

  const resetCalculator = useCallback(() => {
    setCalculator({
      open: false,
      operator: null,
      baseValue: null,
      steps: []
    });
  }, []);

  const restoreNumberDisplay = useCallback(() => {
    setDisplayValue(value === 0 || value === '0' ? '' : String(value ?? ''));
  }, [value]);

  const resolveQuickMathResult = useCallback(() => {
    if (!calculator.open || !calculator.operator || calculator.baseValue === null) {
      return null;
    }

    const operand = parseQuickMathOperand(displayValue);

    if (operand === null) {
      return null;
    }

    const result = applyQuickMath({
      baseValue: calculator.baseValue,
      operator: calculator.operator,
      operand
    });

    if (!result.ok) {
      return null;
    }

    return result.value;
  }, [calculator, displayValue]);

  const startQuickMath = useCallback((operator: QuickMathOperator, baseValue: number) => {
    setCalculator({
      open: true,
      operator,
      baseValue,
      steps: []
    });
    setDisplayValue('');
    setIsFocused(true);
  }, []);

  const commitQuickMath = useCallback(
    ({ keepFocus = false }: { keepFocus?: boolean } = {}) => {
      const resolvedValue = resolveQuickMathResult();

      if (resolvedValue === null) {
        return false;
      }

      handleChange(resolvedValue as T);
      onCommit?.(resolvedValue as T);
      setDisplayValue(String(resolvedValue));
      setIsFocused(keepFocus);
      resetCalculator();
      return true;
    },
    [onCommit, resetCalculator, resolveQuickMathResult]
  );

  const advanceQuickMath = useCallback(
    (operator: QuickMathOperator) => {
      if (!calculator.open) {
        return false;
      }

      if (!displayValue.trim()) {
        setCalculator(currentCalculator => ({
          ...currentCalculator,
          operator
        }));
        return true;
      }

      const resolvedValue = resolveQuickMathResult();

      if (resolvedValue === null) {
        return false;
      }

      const operand = parseQuickMathOperand(displayValue);

      if (operand === null || calculator.operator === null) {
        return false;
      }

      setCalculator({
        open: true,
        operator,
        baseValue: resolvedValue,
        steps: [
          ...calculator.steps,
          {
            operator: calculator.operator,
            operand,
            result: resolvedValue
          }
        ]
      });
      setDisplayValue('');
      setIsFocused(true);
      return true;
    },
    [calculator.open, calculator.operator, calculator.steps, displayValue, resolveQuickMathResult]
  );

  const cancelQuickMath = useCallback(
    ({ keepFocus = true }: { keepFocus?: boolean } = {}) => {
      resetCalculator();
      restoreNumberDisplay();
      setIsFocused(keepFocus);
    },
    [resetCalculator, restoreNumberDisplay]
  );

  useEffect(() => {
    if (type === 'number' && !isFocused) {
      setDisplayValue(formatNumericValue(value));
    } else if (type === 'text' && (!isFocused || commitOnBlur)) {
      setDisplayValue(value !== undefined && value !== null ? String(value) : '');
    } else if (type !== 'number' && type !== 'text') {
      setDisplayValue(value !== undefined && value !== null ? String(value) : '');
    }
  }, [commitOnBlur, formatNumericValue, isFocused, type, value]);

  useEffect(() => {
    if (!calculator.open) {
      return;
    }

    const frame = requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });

    return () => cancelAnimationFrame(frame);
  }, [calculator.open]);

  const normalizedSelectValue = useMemo(() => {
    if (value === undefined || value === null || !options?.length) return undefined;
    const matchedOption = options.find(option => String(option.value) === String(value));
    return matchedOption ? String(matchedOption.value) : undefined;
  }, [options, value]);

  const multiselectValues = useMemo(() => {
    if (!Array.isArray(value)) return [];
    return value.map(item => String(item));
  }, [value]);

  const multiselectLabels = useMemo(() => {
    if (!options?.length) return [];
    const selectedValueSet = new Set(multiselectValues);
    return options.filter(option => selectedValueSet.has(String(option.value)));
  }, [multiselectValues, options]);

  const handleChange = (nextValue: any) => {
    onValueChange?.(nextValue);
  };

  const formatQuickMathDisplayValue = useCallback(
    (nextValue: number) => formatNumericValue(nextValue as T),
    [formatNumericValue]
  );

  const quickMathDisplaySteps = useMemo(
    () => calculator.steps.map((step, index) => ({ step, index })).reverse(),
    [calculator.steps]
  );

  const renderDisabledValue = (content: React.ReactNode, align: 'left' | 'right' = 'left') => (
    <div
      className={cn(
        'flex min-h-[24px] w-full items-center overflow-hidden text-ellipsis whitespace-nowrap px-2 py-1 text-[13px] font-semibold',
        content === '/ /' && 'text-black',
        fieldError && 'border-b border-b-red-500',
        align === 'right' ? 'justify-end text-right' : 'text-left'
      )}
    >
      {content}
    </div>
  );

  return withErrorTooltip(
    <div className={cn('flex w-full flex-col', className)}>
      {type === 'select' &&
        (disabled ? (
          renderDisabledValue(options?.find(option => String(option.value) === String(value))?.label ?? '')
        ) : (
          <Select
            value={normalizedSelectValue}
            onValueChange={selectedValue => {
              const selectedOption = options?.find(option => String(option.value) === selectedValue);
              handleChange(selectedOption?.value ?? selectedValue);
            }}
          >
            <SelectTrigger
              aria-invalid={!!fieldError}
              className={cn(
                baseFieldClassName,
                'focus:ring-0 focus:ring-offset-0 focus-visible:ring-offset-0',
                fieldError && errorFieldClassName,
                inputClassName
              )}
            >
              <SelectValue placeholder={placeholder || ''} />
            </SelectTrigger>
            <SelectContent className='max-h-[300px] rounded-sm'>
              {options?.map((option, index) => (
                <SelectItem key={getCellOptionKey(option, index)} value={String(option.value)} className='text-[14px]'>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ))}

      {type === 'checkbox' && (
        <Checkbox
          checked={Boolean(value)}
          onCheckedChange={handleChange}
          disabled={disabled}
          aria-invalid={!!fieldError}
          className={cn('size-4 rounded-sm border-gray-300', fieldError && 'border-red-500')}
        />
      )}

      {type === 'date' &&
        (disabled ? (
          renderDisabledValue(formatDateValue(value) || '/ /')
        ) : (
          <FormDatePickerV2
            value={value}
            placeholder={placeholder}
            invalid={!!fieldError}
            inputClassName={cn(fieldError && errorFieldClassName, inputClassName)}
            onValueChange={nextValue => {
              handleChange(nextValue as T);
              onCommit?.(nextValue as T);
            }}
          />
        ))}

      {type === 'multiselect' &&
        (disabled ? (
          renderDisabledValue(
            multiselectLabels.length > 0 ? multiselectLabels.map(option => option.label).join(', ') : '',
            'left'
          )
        ) : (
          <Popover open={multiselectOpen} onOpenChange={setMultiselectOpen}>
            <PopoverTrigger asChild>
              <button
                type='button'
                aria-invalid={!!fieldError}
                className={cn(
                  baseFieldClassName,
                  'flex min-h-[var(--erp-control-height)] items-center justify-between gap-2 text-left outline-none',
                  fieldError && errorFieldClassName,
                  inputClassName
                )}
              >
                <span className='flex min-w-0 flex-wrap gap-1'>
                  {multiselectLabels.length > 0 ? (
                    multiselectLabels.map((option, index) => (
                      <span
                        key={getCellOptionKey(option, index)}
                        className='max-w-full truncate rounded-sm bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700'
                      >
                        {option.label}
                      </span>
                    ))
                  ) : (
                    <span className='text-black/60'>{placeholder || ''}</span>
                  )}
                </span>
                <ChevronDown className='size-4 shrink-0 text-black/50' />
              </button>
            </PopoverTrigger>
            <PopoverContent className='w-[var(--radix-popover-trigger-width)] p-0' align='start'>
              <Command>
                <CommandInput placeholder='Tìm kiếm...' />
                <CommandList>
                  <CommandEmpty>Không có lựa chọn phù hợp</CommandEmpty>
                  {options?.map((option, index) => {
                    const optionValue = String(option.value);
                    const checked = multiselectValues.includes(optionValue);
                    return (
                      <CommandItem
                        key={getCellOptionKey(option, index)}
                        value={`${option.label} ${optionValue}`}
                        onSelect={() => {
                          const nextValues = checked
                            ? multiselectValues.filter(item => item !== optionValue)
                            : [...multiselectValues, optionValue];
                          const nextSelected = options
                            .filter(currentOption => nextValues.includes(String(currentOption.value)))
                            .map(currentOption => currentOption.value);
                          handleChange(nextSelected as T);
                        }}
                      >
                        <Checkbox checked={checked} className='pointer-events-none size-4 rounded-sm border-gray-300' />
                        <span className='truncate'>{option.label}</span>
                      </CommandItem>
                    );
                  })}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        ))}

      {(type === 'text' || type === 'number') &&
        (disabled ? (
          renderDisabledValue(
            type === 'number' ? formatNumericValue(value) : value !== undefined && value !== null ? String(value) : '',
            type === 'number' ? 'right' : 'left'
          )
        ) : (
          <Popover open={type === 'number' && calculator.open}>
            <PopoverAnchor asChild>
              <div className='w-full'>
                <Input
                  ref={inputRef}
                  type='text'
                  autoComplete='off'
                  value={
                    type === 'number'
                      ? isFocused
                        ? displayValue
                        : formatNumericValue(value)
                      : commitOnBlur || isFocused
                        ? displayValue
                        : value !== undefined && value !== null
                          ? String(value)
                          : ''
                  }
                  onChange={event => {
                    const inputValue = event.target.value;
                    if (type === 'number') {
                      let processedValue = inputValue;
                      if ((displayValue === '0' || displayValue === '') && inputValue.length > 0) {
                        if (inputValue.match(/^0+[1-9]/)) {
                          processedValue = inputValue.replace(/^0+/, '');
                        } else if (inputValue === '0' && displayValue === '') {
                          processedValue = '0';
                        } else if (displayValue === '0' && inputValue.length === 1 && /[1-9]/.test(inputValue)) {
                          processedValue = inputValue;
                        }
                      }

                      setDisplayValue(processedValue);

                      if (calculator.open) {
                        return;
                      }

                      const parsedValue = parseNumber(processedValue.replace(/[^\d.,-]/g, ''));
                      if (!commitOnBlur) {
                        handleChange(parsedValue);
                      }
                      return;
                    }

                    setDisplayValue(inputValue);
                    if (!commitOnBlur) {
                      handleChange(inputValue as T);
                    }
                  }}
                  onFocus={() => {
                    setIsFocused(true);
                    if (type === 'number') {
                      restoreNumberDisplay();
                    } else {
                      setDisplayValue(value !== undefined && value !== null ? String(value) : '');
                    }
                  }}
                  onBlur={event => {
                    if (type === 'number') {
                      if (calculator.open) {
                        const committed = commitQuickMath({ keepFocus: false });
                        if (!committed) {
                          cancelQuickMath({ keepFocus: false });
                        }
                        return;
                      }

                      setIsFocused(false);
                      const numericValue = parseFloat(event.target.value.replace(/[^\d.-]/g, '')) || null;
                      handleChange(numericValue);
                      onCommit?.(numericValue as T);
                      return;
                    }

                    setIsFocused(false);
                    if (commitOnBlur) {
                      handleChange(event.target.value as T);
                      onCommit?.(event.target.value as T);
                    }
                  }}
                  onKeyDown={event => {
                    const key = event.key.toLowerCase();
                    if ((event.ctrlKey || event.metaKey) && key === 'a') {
                      event.stopPropagation();
                      return;
                    }

                    if (type === 'number' && calculator.open && isQuickMathOperator(event.key)) {
                      event.preventDefault();
                      event.stopPropagation();
                      advanceQuickMath(event.key);
                      return;
                    }

                    if (type === 'number' && quickMathEnabled && !event.altKey && isQuickMathOperator(event.key)) {
                      const quickMathBaseValue = resolveQuickMathBaseValue(isFocused ? displayValue : value);

                      if (!canStartQuickMath(quickMathBaseValue) || quickMathBaseValue === null) {
                        return;
                      }

                      event.preventDefault();
                      event.stopPropagation();
                      startQuickMath(event.key, quickMathBaseValue);
                      return;
                    }

                    if (event.key === ' ') {
                      event.stopPropagation();
                    }

                    if (type === 'number' && calculator.open) {
                      if (event.key === 'Escape') {
                        event.preventDefault();
                        cancelQuickMath();
                        return;
                      }

                      if (event.key === 'Enter') {
                        event.preventDefault();
                        if (!commitQuickMath({ keepFocus: true })) {
                          return;
                        }
                        return;
                      }
                    }

                    if (event.key === 'Enter') {
                      event.currentTarget.blur();
                    }
                  }}
                  placeholder={placeholder}
                  aria-invalid={!!fieldError}
                  className={cn(
                    baseFieldClassName,
                    type === 'number' && 'text-right',
                    fieldError && errorFieldClassName,
                    inputClassName
                  )}
                />
              </div>
            </PopoverAnchor>

            {type === 'number' && calculator.open && calculator.operator && calculator.baseValue !== null ? (
              <PopoverContent
                align='start'
                side='bottom'
                sideOffset={4}
                onOpenAutoFocus={event => event.preventDefault()}
                className={quickMathPopoverClassName}
              >
                <div className='flex flex-col gap-1.5'>
                  <div data-testid='quick-math-current-row' className={quickMathCurrentRowClassName}>
                    <span data-testid='quick-math-current-operator' className={quickMathCurrentOperatorClassName}>
                      {calculator.operator}
                    </span>
                  </div>

                  {quickMathDisplaySteps.map(({ step, index }) => (
                    <div
                      key={`${step.operator}-${index}-${step.result}`}
                      data-testid={`quick-math-step-${index}-row`}
                      className={quickMathHistoryRowClassName}
                    >
                      <span
                        data-testid={`quick-math-step-${index}-operator`}
                        className={quickMathHistoryOperatorClassName}
                      >
                        {step.operator}
                      </span>
                      <span className={quickMathHistoryValuesClassName}>
                        <span className={quickMathHistoryOperandClassName}>
                          {formatQuickMathDisplayValue(step.operand)}
                        </span>
                        <span
                          data-testid={`quick-math-step-${index}-result`}
                          className={quickMathHistoryResultClassName}
                        >
                          {formatQuickMathDisplayValue(step.result)}
                        </span>
                      </span>
                    </div>
                  ))}

                  <div data-testid='quick-math-current-result' className={quickMathCurrentResultClassName}>
                    {formatQuickMathDisplayValue(calculator.baseValue)}
                  </div>
                </div>
              </PopoverContent>
            ) : null}
          </Popover>
        ))}

      {fieldError && errorDisplay === 'message' && (
        <span className='mt-1 block text-xs text-red-500'>{fieldError}</span>
      )}
    </div>
  );
}

const CellFieldV2 = React.memo(CellFieldV2Component) as typeof CellFieldV2Component;

export default CellFieldV2;
